import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getProveedores, createProveedor, updateProveedor, deleteProveedor } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';

const TIPOS_DOC = ['RUC', 'DNI', 'CE', 'OTRO'];

const ProveedoresPage = () => {
  const { empresaActual } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    tipo_doc: 'RUC', nro_doc: '', razon_social: '', contacto_nombre: '',
    telefono: '', email: '', direccion: '', dias_credito: 30, activo: true
  });

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await getProveedores(empresaActual.id);
      setItems(res.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData({
      tipo_doc: 'RUC', nro_doc: '', razon_social: '', contacto_nombre: '',
      telefono: '', email: '', direccion: '', dias_credito: 30, activo: true
    });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.razon_social) {
      toast.error('La razón social es requerida');
      return;
    }

    const payload = { ...formData, empresa_id: empresaActual.id };

    try {
      if (editingItem) {
        await updateProveedor(editingItem.id, payload);
        toast.success('Proveedor actualizado');
      } else {
        await createProveedor(payload);
        toast.success('Proveedor creado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    try {
      await deleteProveedor(id);
      toast.success('Proveedor eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (!empresaActual) {
    return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-emerald-600" />
          <h1 className="text-2xl font-bold text-gray-900">Proveedores</h1>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="new-proveedor-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay proveedores</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo/Nro Doc</TableHead>
                  <TableHead>Razón Social</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Días Crédito</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.tipo_doc}: {item.nro_doc || '-'}</TableCell>
                    <TableCell className="font-medium">{item.razon_social}</TableCell>
                    <TableCell>{item.contacto_nombre || item.telefono || '-'}</TableCell>
                    <TableCell>{item.dias_credito}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar' : 'Nuevo'} Proveedor</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo Doc</Label>
                <Select value={formData.tipo_doc} onValueChange={(v) => setFormData({ ...formData, tipo_doc: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOC.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nro. Documento</Label>
                <Input value={formData.nro_doc} onChange={(e) => setFormData({ ...formData, nro_doc: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Razón Social *</Label>
              <Input value={formData.razon_social} onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contacto</Label>
                <Input value={formData.contacto_nombre} onChange={(e) => setFormData({ ...formData, contacto_nombre: e.target.value })} />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div>
              <Label>Días Crédito</Label>
              <Input type="number" value={formData.dias_credito} onChange={(e) => setFormData({ ...formData, dias_credito: parseInt(e.target.value) || 0 })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProveedoresPage;
